import {useCallback, useEffect, useRef, useState} from 'react'

import {ColumnSettings, TableColumn} from '../types.js'
import {getColumnIds} from '../utils.js'

/**
 * What a table stores in localStorage[`_${tableName}`]:
 * - columns: width and visibility the user chose, by column id (see getColumnId)
 * - sort: the column the user sorted on
 * - options: the option chosen in a column's dropdown, by column id
 *
 * Other keys are kept as they are: callers with a legacy dropdown (`localStorageKey`) read their option from the top
 * level of this object, the Table keeps that key in sync with options.
 */
export interface StoredTableSettings {
    v: 2
    columns: Record<string, ColumnSettings>
    sort?: {id: string, desc: boolean}
    options: Record<string, string>
    [legacyKey: string]: unknown
}

const version = 2

export function getStorageKey(tableName: string | undefined) {
    // Tables without tableName have always shared `_undefined`
    return `_${tableName}`
}

function emptySettings(): StoredTableSettings {
    return {v: version, columns: {}, options: {}}
}

function isObject(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value)
}

function readStored(tableName: string | undefined): Record<string, unknown> | null {
    try {
        const value = JSON.parse(localStorage.getItem(getStorageKey(tableName)) || 'null')

        return isObject(value) ? value : null
    } catch {
        return null
    }
}

function writeStored(tableName: string | undefined, settings: Record<string, unknown>) {
    try {
        localStorage.setItem(getStorageKey(tableName), JSON.stringify(settings))
    } catch {
        // Storage full or blocked: the settings only last until the page is reloaded
    }
}

/**
 * What is stored for a table, else (once, when the table got a new tableName) what is stored under its legacy
 * tableName. copied tells the caller to write it under the new key.
 */
function readStoredWithLegacy(tableName: string | undefined, legacyTableName?: string): {stored: Record<string, unknown> | null, copied: boolean} {
    const stored = readStored(tableName)

    if (stored || legacyTableName === undefined || legacyTableName === tableName) {
        return {stored, copied: false}
    }

    const legacy = readStored(legacyTableName)

    return {stored: legacy, copied: !!legacy}
}

function pickColumnSettings(value: Record<string, unknown>): ColumnSettings {
    const settings: ColumnSettings = {}

    if (typeof value.width === 'number' && Number.isFinite(value.width)) {
        settings.width = value.width
    }

    if (typeof value.visible === 'boolean') {
        settings.visible = value.visible
    }

    return settings
}

/**
 * Turns what is stored into the current schema. Version 1 stored each column under its title (translated, so it
 * changes with the language), the sort as an index into columns (wrong once columns are added or removed) and
 * dropdown options under keys the callers chose. Both are mapped to column ids using the current columns. A column
 * title that matches no column now (a column that is only there for some resellers) stays under its title, see
 * getStoredColumn.
 */
export function migrateSettings(stored: Record<string, unknown> | null, columns: TableColumn[]): StoredTableSettings {
    if (!stored) {
        return emptySettings()
    }

    if (stored.v === version) {
        const sort = isObject(stored.sort) && typeof stored.sort.id === 'string' ? {id: stored.sort.id, desc: !!stored.sort.desc} : undefined

        return remapPreviousIds({
            ...stored,
            v: version,
            columns: isObject(stored.columns) ? stored.columns as Record<string, ColumnSettings> : {},
            options: isObject(stored.options) ? stored.options as Record<string, string> : {},
            sort
        }, columns)
    }

    const ids = getColumnIds(columns)
    const settings = emptySettings()
    const legacy: Record<string, unknown> = {}

    for (const [key, value] of Object.entries(stored)) {
        if (key === 'sortColumnIndex' || key === 'sortDecending') {
            continue
        }

        if (isObject(value) && ('visible' in value || 'width' in value)) {
            const index = columns.findIndex((column) => column.title === key)

            settings.columns[index > -1 ? ids[index] : key] = pickColumnSettings(value)
        } else {
            legacy[key] = value
        }
    }

    const sortIndex = stored.sortColumnIndex

    if (typeof sortIndex === 'number' && ids[sortIndex]) {
        settings.sort = {id: ids[sortIndex], desc: !!stored.sortDecending}
    }

    columns.forEach((column, index) => {
        const legacyKey = column.dropdown?.localStorageKey || column.dropdown?.legacyKey

        if (legacyKey && typeof legacy[legacyKey] === 'string') {
            settings.options[ids[index]] = legacy[legacyKey] as string
        }
    })

    // The legacy keys stay at the top level, callers still read them there
    return {...legacy, ...settings}
}

/**
 * Columns that got an explicit id were stored under their previous id (key, else title) before. Moves those settings
 * (width, visibility, sort, dropdown option) to the new id, unless the new id has settings already or another column
 * now uses the previous id. Returns the same object when nothing moved.
 */
export function remapPreviousIds(settings: StoredTableSettings, columns: TableColumn[]): StoredTableSettings {
    const ids = getColumnIds(columns)
    const taken = new Set(ids)
    let result = settings

    columns.forEach((column, index) => {
        const id = ids[index]
        const previous = column.key || column.title

        if (!column.id || !previous || previous === id || taken.has(previous)) {
            return
        }

        if (result.columns[previous] && !result.columns[id]) {
            const {[previous]: moved, ...rest} = result.columns
            result = {...result, columns: {...rest, [id]: moved}}
        }

        if (result.options[previous] !== undefined && result.options[id] === undefined) {
            const {[previous]: moved, ...rest} = result.options
            result = {...result, options: {...rest, [id]: moved}}
        }

        if (result.sort?.id === previous) {
            result = {...result, sort: {...result.sort, id}}
        }
    })

    return result
}

/** The stored settings of a column, by id or (not migrated yet, see migrateSettings) by title */
export function getStoredColumn(settings: StoredTableSettings, id: string, title?: string): ColumnSettings | undefined {
    return settings.columns[id] ?? (title ? settings.columns[title] : undefined)
}

export function hasStoredSettings(settings: StoredTableSettings) {
    return Object.keys(settings.columns).length > 0 || !!settings.sort || Object.keys(settings.options).length > 0
}

/**
 * Shows or hides a column of a table that isn't mounted, as if the user chose it in the table settings (used to show
 * the branch columns once branches are set up). A table that wasn't opened since the settings got ids still stores
 * its columns by title, so the title is set too. With legacyTableName, a table that has no settings under tableName yet
 * starts from the ones under legacyTableName (see useTableSettings).
 */
export function setStoredColumnVisible(tableName: string | undefined, column: {id: string, title: string}, visible: boolean, legacyTableName?: string) {
    const stored = readStoredWithLegacy(tableName, legacyTableName).stored || emptySettings()

    if (stored.v === version) {
        const columns = isObject(stored.columns) ? {...stored.columns} as Record<string, ColumnSettings> : {}

        columns[column.id] = {...columns[column.id], visible}

        if (columns[column.title]) {
            columns[column.title] = {...columns[column.title], visible}
        }

        writeStored(tableName, {...stored, columns})
    } else {
        // Version 1, migrateSettings moves it to the id when the table is opened
        const stored1 = isObject(stored[column.title]) ? stored[column.title] as Record<string, unknown> : {}

        writeStored(tableName, {...stored, [column.title]: {...stored1, visible}})
    }
}

function loadSettings(tableName: string | undefined, legacyTableName: string | undefined, columns: TableColumn[]) {
    const {stored, copied} = readStoredWithLegacy(tableName, legacyTableName)
    const settings = migrateSettings(stored, columns)

    // Stored again when it came from the legacy key or the migration changed it (version 1, columns' new ids)
    const migrated = copied || (!!stored && JSON.stringify(settings) !== JSON.stringify(stored))

    return {tableName, settings, migrated}
}

/**
 * The settings of one table, loaded (and migrated) once and written to localStorage on every change. Writes happen
 * before the re-render, so a legacy sort function reading localStorage sees the new option.
 *
 * legacyTableName: the tableName this table stored its settings under before. While there's nothing under tableName,
 * the settings under legacyTableName are copied to it (the legacy key is left as it is, another table may use it).
 */
export default function useTableSettings(tableName: string | undefined, columns: TableColumn[], legacyTableName?: string) {
    const [state, setState] = useState(() => loadSettings(tableName, legacyTableName, columns))

    let current = state

    // Another tableName is another table: load its settings
    if (state.tableName !== tableName) {
        current = loadSettings(tableName, legacyTableName, columns)
        setState(current)
    }

    const settingsRef = useRef(current.settings)
    settingsRef.current = current.settings

    // Store a migration once, so it isn't migrated again with other columns
    useEffect(() => {
        if (current.migrated) {
            writeStored(tableName, current.settings)
        }
    }, [current])

    const update = useCallback((change: (settings: StoredTableSettings) => StoredTableSettings) => {
        const settings = change(settingsRef.current)

        if (settings === settingsRef.current) {
            return
        }

        settingsRef.current = settings
        writeStored(tableName, settings)
        setState({tableName, settings, migrated: false})
    }, [tableName])

    /** Merges these settings into the stored settings of the columns, by column id */
    const updateColumns = useCallback((changes: Record<string, ColumnSettings>) => {
        update((settings) => {
            const columns = {...settings.columns}

            for (const [id, change] of Object.entries(changes)) {
                columns[id] = {...columns[id], ...change}
            }

            return {...settings, columns}
        })
    }, [update])

    /** Forgets the visibility the user chose for these columns (by id and title), so the caller's visible applies */
    const clearVisible = useCallback((keys: string[]) => {
        update((settings) => {
            const stored = keys.filter((key) => settings.columns[key]?.visible !== undefined)

            if (!stored.length) {
                return settings
            }

            const columns = {...settings.columns}

            stored.forEach((key) => {
                const {visible, ...rest} = columns[key] // eslint-disable-line @typescript-eslint/no-unused-vars
                columns[key] = rest
            })

            return {...settings, columns}
        })
    }, [update])

    const setSort = useCallback((sort: {id: string, desc: boolean}) => {
        update((settings) => ({...settings, sort}))
    }, [update])

    /** Stores the dropdown option of a column, also under its legacy localStorageKey */
    const setOption = useCallback((id: string, value: string, legacyKey?: string) => {
        update((settings) => {
            const next = {...settings, options: {...settings.options, [id]: value}}

            if (legacyKey) {
                next[legacyKey] = value
            }

            return next
        })
    }, [update])

    // Stores an empty object instead of removing it: legacy sort functions JSON.parse it
    const reset = useCallback(() => {
        update(() => emptySettings())
    }, [update])

    return {settings: current.settings, updateColumns, clearVisible, setSort, setOption, reset}
}
